import React, { useEffect, useState } from 'react';
import { Flame, MessageCircle, Heart } from 'lucide-react';
import { API, getAuthHeaders } from '../App';

/**
 * TrendingThisWeek — compact widget for the Dashboard showing the 3 most-engaged
 * posts from the last 7 days. Silently renders nothing if no posts qualify.
 */
export default function TrendingThisWeek({ onOpenPost }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/social/feed/trending?limit=3&days=7`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setPosts(data.posts || []);
      } catch (_err) {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!posts.length) return null;

  const totalReactions = (p) => {
    const r = p.reactions || {};
    let sum = 0;
    Object.values(r).forEach((v) => {
      if (Array.isArray(v)) sum += v.length;
      else if (typeof v === 'number') sum += v;
    });
    return sum + (Array.isArray(p.likes) ? p.likes.length : 0);
  };

  const snippet = (p) => {
    if (p.caption) return p.caption;
    if (p.post_type === 'round' && p.course_name) {
      return `Posted a round at ${p.course_name}${p.score ? ` — shot ${p.score}` : ''}`;
    }
    return 'View post';
  };

  const thumb = (p) => {
    if (Array.isArray(p.media_urls) && p.media_urls.length) return p.media_urls[0];
    return p.photo_url || null;
  };

  return (
    <div className="mb-6" data-testid="trending-this-week">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-heading font-black text-base text-bundle-green flex items-center gap-1.5">
          <Flame className="w-4 h-4 text-orange-500" />
          Trending This Week
        </h2>
        <span className="text-[10px] text-gray-400 uppercase tracking-wide">Last 7 days</span>
      </div>

      <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory">
        {posts.map((p) => {
          const img = thumb(p);
          const reactions = totalReactions(p);
          const comments = p.comment_count || 0;
          return (
            <button
              key={p.post_id}
              type="button"
              onClick={() => onOpenPost && onOpenPost(p.post_id)}
              data-testid={`trending-post-${p.post_id}`}
              className="snap-start flex-shrink-0 w-[240px] bg-white rounded-2xl border border-bundle-cream-dark shadow-sm p-3 text-left hover:shadow-md active:scale-[0.98] transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                {p.user_picture ? (
                  <img src={p.user_picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-bundle-green/20 flex items-center justify-center text-[11px] font-bold text-bundle-green">
                    {(p.user_name || 'M').charAt(0)}
                  </div>
                )}
                <span className="text-xs font-bold text-bundle-green truncate flex-1">
                  {p.user_name || 'Member'}
                </span>
              </div>

              {img && (
                <img
                  src={img}
                  alt=""
                  className="w-full h-24 object-cover rounded-lg mb-2"
                  loading="lazy"
                />
              )}

              <p className="text-xs text-gray-700 line-clamp-2 mb-2 min-h-[2rem]">
                {snippet(p)}
              </p>

              <div className="flex items-center gap-3 text-[11px] text-gray-500">
                <span className="flex items-center gap-1">
                  <Heart className="w-3 h-3 text-rose-500" />
                  {reactions}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-3 h-3 text-emerald-600" />
                  {comments}
                </span>
                <span className="ml-auto text-[10px] font-bold text-orange-500 flex items-center gap-0.5">
                  <Flame className="w-3 h-3" />
                  {p.engagement_score}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
